/**
 * /pulido — Centro de pulido de datos.
 *
 * Reescrito 2026-05-04: se eliminaron 23 secciones del scaffold F3.1 que
 * devolvían mocks (counts=0, botones stub) y los chips de "filtro por módulo"
 * que referenciaban Compras/Máquinas/Documentos/Facturas/Usuarios/DB sin
 * tener queries reales detrás.
 *
 * Lo que queda: las 11 categorías reales del D-T51, agrupadas por urgencia
 * (Crítica / Alta / Media / Baja), con KPI cards arriba y filtro por chips.
 * Cada sección carga su detalle on-demand y permite navegar al destino con
 * foco en el campo a editar.
 */
import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import backend from "../../backend";
import AuthContext from "../../AuthContext";
import useTitle from "../../hooks/useTitle";

// ─── Catálogo de categorías reales ──────────────────────────────────────────
// Cada categoría tiene urgencia (critica/alta/media/baja), persisted (si vive
// en collection Alert vs cálculo runtime) y target (a dónde navegar al click).
const CATEGORIES = [
  // 🔴 Crítica — datos rotos o inconsistencias graves
  { type: "piece_huerfano",            urgencia: "critica", icon: "🔍", label: "Pieces huérfanos",
    desc: "Items que apuntan a piezas borradas. Bloquean cálculos y reportes.",
    persisted: false, target: "workOrder" },
  { type: "entidad_rut_duplicado",     urgencia: "critica", icon: "🚨", label: "Duplicados de RUT",
    desc: "RUTs únicos compartidos por 2+ entidades. Data corrupta — afecta facturación.",
    persisted: true, target: "entity" },
  { type: "cierre_sin_documento",      urgencia: "critica", icon: "💸", label: "Cierres sin documento",
    desc: "OTs que quedaron entregadas/facturadas por cascada sin guía ni factura registrada. Plata invisible — regularizar el documento.",
    persisted: true, target: "workOrder" },
  { type: "hook_fallido",              urgencia: "critica", icon: "⚠️", label: "Procesos automáticos fallidos",
    desc: "Un proceso post-guardado falló en silencio: el documento se guardó pero un efecto derivado no corrió. Revisar el documento afectado.",
    persisted: true, target: "workOrder" },

  // 🟠 Alta — afecta facturación o producción activa
  { type: "cot_facturado_sin_dte",     urgencia: "alta", icon: "🧾", label: "OTs facturadas sin DTE",
    desc: "OTs en estado 'facturado' pero sin N° de factura cargado. Riesgo SII.",
    persisted: false, target: "workOrder" },
  { type: "cot_sin_cliente_drive",     urgencia: "alta", icon: "❓", label: "OTs sin cliente identificado",
    desc: "OTs importadas del Drive sin cliente asignado.",
    persisted: true, target: "workOrder" },
  { type: "piece_fuera_catalogo",      urgencia: "alta", icon: "🏷️", label: "Piezas fuera de catálogo Drive",
    desc: "Piezas en uso (CO/WO activas) que no figuran en el Drive oficial 'Catálogo y Precios'.",
    persisted: false, target: "piece" },

  // 🟡 Media — catálogo incompleto, no bloquea operación
  { type: "piece_sin_precio",          urgencia: "media", icon: "💰", label: "Piezas sin precio",
    desc: "Piezas del catálogo sin precio cargado. Necesario para cotizar.",
    persisted: false, target: "piece" },
  { type: "sku_no_registrado",         urgencia: "media", icon: "🆕", label: "SKU no registrado",
    desc: "SKUs aparecen en items pero no existen en el catálogo.",
    persisted: false, target: "piece" },

  // ⚪ Baja — info / saneamiento de importaciones viejas
  { type: "item_sin_sku",              urgencia: "baja", icon: "📦", label: "Items sin SKU",
    desc: "Items con descripción literal sin SKU asignado.",
    persisted: false, target: "workOrder" },
  { type: "entidad_sin_datos",         urgencia: "baja", icon: "🏢", label: "Entidades sin datos",
    desc: "Clientes importados con datos mínimos (falta RUT/email/teléfono).",
    persisted: true, target: "entity" },
  { type: "entidad_posible_duplicado", urgencia: "baja", icon: "🔁", label: "Posibles duplicados de entidad",
    desc: "Entidades con nombres similares — posibles aliases del mismo cliente.",
    persisted: true, target: "entity" },
];

const URGENCIA_META = {
  critica: { label: "Crítica", color: "#e74c3c", desc: "Errores de datos. Resolver lo antes posible." },
  alta:    { label: "Alta",    color: "#f39c12", desc: "Afecta facturación o producción activa." },
  media:   { label: "Media",   color: "#3498db", desc: "Catálogo incompleto, mejorar para mejor cotización." },
  baja:    { label: "Baja",    color: "#cbd5e1",    desc: "Info / saneamiento de importaciones viejas." },
};

const fmtCLP = n => n ? `$${Number(n).toLocaleString("es-CL")}` : "—";

// ─── Sub-sección colapsable por categoría ───────────────────────────────────
const CategoryCard = ({ category, count, onResolved, onUndoableResolve }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await backend.get(`pulido/by-type/${category.type}`);
      setItems(r.data.items || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [category.type]);

  useEffect(() => {
    if (open && items === null) loadItems();
  }, [open, items, loadItems]);

  const navigateTo = useCallback(item => {
    const type = category.type;
    const itemIdx = item.sub_id ? item.sub_id.split("-").slice(-1)[0] : null;
    const originBase = item.origin === "CO" ? "/customerOrders" : "/workOrders";
    const originId = item.origin_id;
    const originNumber = item.origin_number;

    switch (type) {
      case "entidad_sin_datos": {
        const eid = item.entity?._id;
        if (eid) navigate(`/entities?openId=${eid}&editField=rut`);
        else navigate("/entities");
        return;
      }
      case "entidad_posible_duplicado":
      case "entidad_rut_duplicado": {
        const id1 = item.entity?._id;
        const id2 = item.entityRef2?._id;
        if (id1 && id2) navigate(`/entities?compare=${id1},${id2}`);
        else if (id1) navigate(`/entities?openId=${id1}`);
        else navigate("/entities");
        return;
      }
      case "cierre_sin_documento":
      case "hook_fallido": {
        const wid = item.workOrder?._id;
        const cid = item.customerOrder?._id;
        if (wid) navigate(`/workOrders?openId=${wid}`);
        else if (cid) navigate(`/customerOrders?openId=${cid}`);
        else navigate("/workOrders");
        return;
      }
      case "cot_sin_cliente_drive": {
        const wid = item.workOrder?._id;
        if (wid) navigate(`/workOrders?openId=${wid}&editField=client`);
        else if (item.workOrder?.number) navigate(`/workOrders?openByNumber=${item.workOrder.number}&editField=client`);
        else navigate("/workOrders");
        return;
      }
      case "item_sin_sku":
      case "piece_sin_precio": {
        const focus = type === "item_sin_sku" ? "sku" : "pUnit";
        if (originId && itemIdx != null) {
          navigate(`${originBase}?openId=${originId}&editField=item:${itemIdx}:${focus}`);
        } else if (originNumber) {
          navigate(`${originBase}?openByNumber=${originNumber}`);
        } else {
          navigate(originBase);
        }
        return;
      }
      case "sku_no_registrado": {
        const sku = item.sku;
        if (sku) navigate(`/pieces?action=create&suggestedSku=${encodeURIComponent(sku)}`);
        else navigate("/pieces");
        return;
      }
      case "piece_huerfano": {
        if (originId) navigate(`${originBase}?openId=${originId}`);
        else if (originNumber) navigate(`${originBase}?openByNumber=${originNumber}`);
        else navigate(originBase);
        return;
      }
      case "cot_facturado_sin_dte": {
        if (originId) navigate(`/workOrders?openId=${originId}&editField=factura`);
        else if (originNumber) navigate(`/workOrders?openByNumber=${originNumber}&editField=factura`);
        else navigate("/workOrders");
        return;
      }
      case "piece_fuera_catalogo": {
        const sku = item.sku;
        if (sku) navigate(`/pieces?openBySku=${encodeURIComponent(sku)}`);
        else if (item.origin_id) navigate(`/pieces?openId=${item.origin_id}`);
        else navigate("/pieces");
        return;
      }
      default:
        navigate("/dashboard");
    }
  }, [navigate, category.type]);

  const handleResolve = async (alertItem) => {
    const alertId = String(alertItem._id);
    try {
      await backend.patch(`pulido/alert/${alertId}`, { status: "resolved" });
      setItems(prev => prev.filter(it => String(it._id) !== alertId));
      onResolved?.();
      // Notificar al padre para que muestre toast con undo (7s).
      // Si el usuario click "Deshacer", el padre llamará un PATCH a pending y
      // ejecutará el callback que pasamos para reinsertar el item localmente.
      onUndoableResolve?.({
        alertId,
        snapshot: alertItem,
        categoryLabel: category.label,
        restoreLocal: () => setItems(prev => prev ? [alertItem, ...prev] : [alertItem]),
      });
    } catch {
      // no-op
    }
  };

  const meta = URGENCIA_META[category.urgencia];

  // Renderizar sin items pendientes (✓ resuelto)
  if (count === 0) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "8px 12px", marginBottom: 4,
        background: "#161616", border: "1px solid #1f1f1f", borderRadius: 5,
        opacity: 0.5, fontSize: 11,
      }}>
        <span style={{ width: 10 }} />
        <span style={{ fontSize: 13 }}>{category.icon}</span>
        <span style={{ flex: 1, color: "#aaa" }}>{category.label}</span>
        <span style={{ color: "#2ecc71", fontSize: 10 }}>✓ 0</span>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 6 }}>
      <div onClick={() => setOpen(o => !o)}
        className="puli-cat tap-card"
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "9px 12px", cursor: "pointer",
          background: "#1a1a1a",
          border: `1px solid ${open ? meta.color : "#2e2e2e"}`,
          borderRadius: open ? "5px 5px 0 0" : 5,
        }}>
        <span style={{ width: 10, color: "#999", fontSize: 10 }}>{open ? "▼" : "▶"}</span>
        <span style={{ fontSize: 14 }}>{category.icon}</span>
        <span style={{ flex: 1, fontSize: 12, color: "#ddd" }}>{category.label}</span>
        {category.persisted && (
          <span style={{ fontSize: 9, color: "#aaa", textTransform: "uppercase", letterSpacing: 0.5 }}>
            persistida
          </span>
        )}
        <span style={{
          fontSize: 11, fontWeight: 700,
          color: meta.color,
          background: `${meta.color}22`,
          border: `1px solid ${meta.color}55`,
          padding: "2px 10px", borderRadius: 12,
        }}>{count}</span>
      </div>

      {open && (
        <div style={{
          border: `1px solid ${meta.color}`, borderTop: "none",
          borderRadius: "0 0 5px 5px",
          background: "#161616", padding: 10,
        }}>
          <div style={{ fontSize: 10, color: "#cbd5e1", marginBottom: 8, fontStyle: "italic" }}>
            {category.desc}
          </div>

          {loading && <div style={{ padding: 8, color: "#aaa", fontSize: 11 }}>Cargando…</div>}
          {error && <div style={{ padding: 8, color: "#e74c3c", fontSize: 11 }}>Error: {error}</div>}

          {items && items.length > 0 && (
            <table style={{ tableLayout: "fixed", width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #2e2e2e" }}>
                  {category.persisted ? (
                    <>
                      <th style={thStyle(30)}>ITEM</th>
                      <th style={thStyle(30)}>DETALLE</th>
                      <th style={thStyle()}>RAZÓN</th>
                      <th style={{ ...thStyle(120), textAlign: "right" }}>ACCIÓN</th>
                    </>
                  ) : (
                    <>
                      <th style={thStyle(90)}>SUB-ID</th>
                      <th style={thStyle(95)}>OT/OC</th>
                      <th style={thStyle(20)}>CLIENTE</th>
                      <th style={thStyle(30)}>SKU / DESC</th>
                      <th style={{ ...thStyle(50), textAlign: "right" }}>QTY</th>
                      <th style={{ ...thStyle(), textAlign: "right" }}>$ DETALLE</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.slice(0, 100).map((it, idx) => {
                  if (category.persisted) {
                    const e1 = it.entity?.name || it.data?.drive || it.data?.name
                      || (it.data?.woNumber ? `OT ${it.data.woNumber}` : null) || "—";
                    const e2 = it.entityRef2?.name || it.data?.existente || null;
                    const cot = it.workOrder?.number || it.data?.cot || null;
                    const detalle = e2 ? `↔ ${e2}` : (cot ? `OT ${cot}` : (it.data?.rut ? `RUT ${it.data.rut}` : "—"));
                    return (
                      <tr key={String(it._id || idx)} style={rowStyle()}
                        onClick={() => navigateTo(it)}
                        onMouseEnter={e => e.currentTarget.style.background = "#1f1f1f"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <td style={tdStyle()} title={e1}>{e1}</td>
                        <td style={{ ...tdStyle(), color: "#cbd5e1" }} title={detalle}>{detalle}</td>
                        <td style={{ ...tdStyle(), color: "#cbd5e1", fontSize: 10 }} title={it.observations || it.data?.razon}>
                          {it.observations || it.data?.razon || "—"}
                        </td>
                        <td style={{ textAlign: "right", padding: "5px 8px" }} onClick={e => e.stopPropagation()}>
                          <button onClick={() => handleResolve(it)}
                            title="Marcar como resuelto (podrás deshacer en los próximos 7 segundos)"
                            style={{
                              fontSize: 10, padding: "3px 8px",
                              background: "#0d2a15", color: "#2ecc71",
                              border: "1px solid #1a3a25", borderRadius: 3,
                              cursor: "pointer",
                            }}>✓ Resolver</button>
                        </td>
                      </tr>
                    );
                  }
                  // volátil
                  const skuOrDesc = it.sku || it.desc || it.piece_name || "—";
                  const detail = it.pUnit > 0 ? fmtCLP(it.pUnit) : "—";
                  return (
                    <tr key={String(it.sub_id || idx)} style={rowStyle()}
                      onClick={() => navigateTo(it)}
                      onMouseEnter={e => e.currentTarget.style.background = "#1f1f1f"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      title={`Click → editar en /${category.target === "workOrder" ? "workOrders" : "pieces"}`}>
                      <td style={{ ...tdStyle(), fontFamily: "monospace", color: "#cbd5e1" }}>{it.sub_id || "—"}</td>
                      <td style={{ ...tdStyle(), fontFamily: "monospace" }}>
                        {it.origin === "WO" && `OT #${it.origin_number}`}
                        {it.origin === "CO" && `OC #${it.origin_number}`}
                        {it.origin === "PC" && <span style={{ color: "#aaa", fontStyle: "italic" }}>Pieza</span>}
                        {it.origin_clientOC && (
                          <div style={{ fontSize: 9, color: "#aaa" }}>{it.origin_clientOC}</div>
                        )}
                      </td>
                      <td style={tdStyle()} title={it.client_name}>{it.client_name || "—"}</td>
                      <td style={{ ...tdStyle(), fontFamily: it.sku ? "monospace" : "inherit" }} title={skuOrDesc}>{skuOrDesc}</td>
                      <td style={{ ...tdStyle(), textAlign: "right", color: "#cbd5e1" }}>{it.qty ?? "—"}</td>
                      <td style={{ ...tdStyle(), textAlign: "right", fontSize: 10, color: "#cbd5e1" }}>{detail}</td>
                    </tr>
                  );
                })}
                {items.length > 100 && (
                  <tr><td colSpan={category.persisted ? 4 : 6}
                    style={{ textAlign: "center", padding: 8, color: "#aaa", fontSize: 10, fontStyle: "italic" }}>
                    ... {items.length - 100} más (limitado a 100 por categoría)
                  </td></tr>
                )}
              </tbody>
            </table>
          )}

          {items && items.length === 0 && (
            <div style={{ padding: 14, textAlign: "center", color: "#2ecc71", fontSize: 12 }}>
              ✓ No hay items en esta categoría
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const thStyle = (width) => ({
  padding: "6px 8px", textAlign: "left", fontSize: 9, fontWeight: 700,
  color: "#aaa", textTransform: "uppercase", letterSpacing: ".06em",
  whiteSpace: "nowrap",
  ...(width ? { width: typeof width === "number" ? `${width}%` : width } : {}),
});
const tdStyle = () => ({
  padding: "6px 8px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
});
const rowStyle = () => ({
  cursor: "pointer", borderBottom: "1px solid #1f1f1f",
});

// ─── KPI card por urgencia ──────────────────────────────────────────────────
const KPICard = ({ urgencia, count, active, onClick }) => {
  const meta = URGENCIA_META[urgencia];
  return (
    <div onClick={onClick} className="puli-kpi tap-card" style={{
      flex: "1 1 0", minWidth: 160, padding: "12px 16px",
      background: active ? `${meta.color}22` : "#1a1a1a",
      border: `1px solid ${active ? meta.color : "#2e2e2e"}`,
      borderRadius: 6, cursor: "pointer",
      transition: "background 0.15s",
    }}>
      <div style={{ fontSize: 9, color: "#aaa", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>
        {meta.label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color: meta.color, lineHeight: 1 }}>{count}</div>
      <div style={{ fontSize: 10, color: "#999", marginTop: 4 }}>{meta.desc}</div>
    </div>
  );
};

// ─── Componente principal ──────────────────────────────────────────────────
const Pulido = () => {
  useTitle("Pulido");
  const authContext = useContext(AuthContext);
  const puedeVerPulido = authContext?.can?.("ver_pulido") || false;

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [filtroUrgencia, setFiltroUrgencia] = useState("todas"); // todas | critica | alta | media | baja

  // Toast undo: cuando una alerta se resuelve, queda visible 7s con botón "Deshacer"
  const [undoToast, setUndoToast] = useState(null); // { alertId, snapshot, categoryLabel, restoreLocal, expiresAt }
  const [showResueltas, setShowResueltas] = useState(false);
  const [resueltas, setResueltas] = useState([]);
  const [loadingResueltas, setLoadingResueltas] = useState(false);

  const handleUndoableResolve = useCallback((payload) => {
    setUndoToast({ ...payload, expiresAt: Date.now() + 7000 });
  }, []);

  // Auto-cerrar el toast a los 7s
  useEffect(() => {
    if (!undoToast) return;
    const remaining = undoToast.expiresAt - Date.now();
    if (remaining <= 0) { setUndoToast(null); return; }
    const t = setTimeout(() => setUndoToast(null), remaining);
    return () => clearTimeout(t);
  }, [undoToast]);

  const handleUndo = useCallback(async () => {
    if (!undoToast) return;
    const { alertId, restoreLocal } = undoToast;
    setUndoToast(null);
    try {
      await backend.patch(`pulido/alert/${alertId}`, { status: "pending" });
      restoreLocal?.();
      // Recargar conteos para que el badge de la categoría suba +1
      const r = await backend.get("pulido/all");
      setSummary(r.data.summary);
    } catch {
      alert("No se pudo deshacer. Refrescá la página.");
    }
  }, [undoToast]);

  const loadResueltas = useCallback(async () => {
    setLoadingResueltas(true);
    try {
      const r = await backend.get("pulido/resueltas?limit=30");
      setResueltas(r.data.items || []);
    } catch {
      setResueltas([]);
    } finally {
      setLoadingResueltas(false);
    }
  }, []);

  const restoreResuelta = async (alertId) => {
    try {
      await backend.patch(`pulido/alert/${alertId}`, { status: "pending" });
      setResueltas(prev => prev.filter(a => String(a._id) !== String(alertId)));
      const r = await backend.get("pulido/all");
      setSummary(r.data.summary);
    } catch {
      alert("No se pudo restaurar.");
    }
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await backend.get("pulido/all");
      setSummary(r.data.summary);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Conteos por urgencia
  const conteos = useMemo(() => {
    const acc = { critica: 0, alta: 0, media: 0, baja: 0, total: 0 };
    if (!summary) return acc;
    for (const cat of CATEGORIES) {
      const n = summary[cat.type] || 0;
      acc[cat.urgencia] += n;
      acc.total += n;
    }
    return acc;
  }, [summary]);

  // Categorías filtradas por urgencia
  const categoriasVisibles = useMemo(() => {
    if (filtroUrgencia === "todas") return CATEGORIES;
    return CATEGORIES.filter(c => c.urgencia === filtroUrgencia);
  }, [filtroUrgencia]);

  if (!puedeVerPulido) return <Navigate to="/dashboard" replace />;

  return (
    <div style={{
      padding: "clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background: "#1a1a1a",
      minHeight: "100%", color: "#ddd",
      fontFamily: "Arial, sans-serif", fontSize: 13,
    }}>
      {/* Header — flexWrap: en teléfono los botones bajan de línea sin apretarse */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 220px" }}>
          <div className="mv-screen-title" style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>Pulido de datos</div>
          <div style={{ fontSize: 11, color: "#cbd5e1", marginTop: 3 }}>
            {conteos.total === 0
              ? "Sin alertas pendientes — todos los flujos al día."
              : `${conteos.total} item${conteos.total > 1 ? "s" : ""} pendiente${conteos.total > 1 ? "s" : ""} de revisar`}
            {summary?.calculatedAt && (
              <span style={{ color: "#999" }}>
                {" "}· última ejecución: {new Date(summary.calculatedAt).toLocaleString("es-CL")}
                {summary.cacheHit ? " (caché)" : ""}
              </span>
            )}
          </div>
        </div>
        <button onClick={() => { setShowResueltas(s => !s); if (!showResueltas) loadResueltas(); }}
          style={{
            background: "transparent", color: "#cbd5e1",
            border: "1px solid #444", borderRadius: 5,
            padding: "7px 14px", fontSize: 12, cursor: "pointer",
          }}>
          📋 Ver resueltas
        </button>
        <button onClick={load} disabled={loading}
          style={{
            background: "#3498db", color: "#fff",
            border: "none", borderRadius: 5,
            padding: "7px 16px", fontSize: 12, fontWeight: 700,
            cursor: loading ? "default" : "pointer",
            opacity: loading ? 0.6 : 1,
          }}>
          {loading ? "…" : "↻ Recalcular"}
        </button>
      </div>

      {/* Panel de resueltas recientes (colapsable) */}
      {showResueltas && (
        <div style={{
          marginBottom: 16, padding: "12px 14px",
          background: "#161616", border: "1px solid #2e2e2e", borderRadius: 6,
        }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
            <div style={{ flex: 1, fontSize: 11, fontWeight: 700, color: "#aaa",
              textTransform: "uppercase", letterSpacing: ".05em" }}>
              📋 Alertas resueltas recientes ({resueltas.length})
            </div>
            <button onClick={() => setShowResueltas(false)}
              style={{ background: "transparent", border: "none", color: "#aaa", cursor: "pointer", fontSize: 14 }}>×</button>
          </div>
          {loadingResueltas && <div style={{ color: "#aaa", fontSize: 11 }}>Cargando…</div>}
          {!loadingResueltas && resueltas.length === 0 && (
            <div style={{ color: "#aaa", fontSize: 11, fontStyle: "italic", padding: "6px 0" }}>
              Sin alertas resueltas en los últimos días.
            </div>
          )}
          {resueltas.map(a => {
            const cat = CATEGORIES.find(c => c.type === a.type);
            const e1 = a.entity?.name || a.data?.name || a.data?.drive || "—";
            return (
              <div key={a._id} className="puli-res-row" style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "6px 0", borderBottom: "1px solid #1f1f1f", fontSize: 11,
              }}>
                <span style={{ fontSize: 13 }}>{cat?.icon || "·"}</span>
                <span className="puli-res-cat" style={{ color: "#cbd5e1", minWidth: 200 }}>{cat?.label || a.type}</span>
                <span style={{ flex: 1, color: "#ddd", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {e1}
                </span>
                <span style={{ color: "#999", fontSize: 10 }}>
                  {a.resolvedAt ? new Date(a.resolvedAt).toLocaleString("es-CL") : "—"}
                </span>
                <button onClick={() => restoreResuelta(a._id)}
                  style={{
                    background: "#1a2535", color: "#a78bfa",
                    border: "1px solid #2d2050", borderRadius: 4,
                    padding: "3px 10px", fontSize: 10, fontWeight: 600, cursor: "pointer",
                  }}>↻ Restaurar</button>
              </div>
            );
          })}
        </div>
      )}

      {/* KPI cards por urgencia (clickeables como filtros) */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <KPICard urgencia="critica" count={conteos.critica}
          active={filtroUrgencia === "critica"}
          onClick={() => setFiltroUrgencia(f => f === "critica" ? "todas" : "critica")} />
        <KPICard urgencia="alta" count={conteos.alta}
          active={filtroUrgencia === "alta"}
          onClick={() => setFiltroUrgencia(f => f === "alta" ? "todas" : "alta")} />
        <KPICard urgencia="media" count={conteos.media}
          active={filtroUrgencia === "media"}
          onClick={() => setFiltroUrgencia(f => f === "media" ? "todas" : "media")} />
        <KPICard urgencia="baja" count={conteos.baja}
          active={filtroUrgencia === "baja"}
          onClick={() => setFiltroUrgencia(f => f === "baja" ? "todas" : "baja")} />
      </div>

      {/* Mensajes de estado */}
      {error && (
        <div style={{
          padding: "10px 14px", marginBottom: 12,
          background: "#1f0a0a", border: "1px solid #5a1f1f",
          borderRadius: 6, color: "#e74c3c", fontSize: 12,
        }}>
          ⚠ Error al cargar: {error}
        </div>
      )}

      {loading && !summary && (
        <div style={{ padding: 20, textAlign: "center", color: "#aaa", fontSize: 12 }}>
          Calculando inconsistencias…
        </div>
      )}

      {summary && conteos.total === 0 && (
        <div style={{
          padding: 20, textAlign: "center",
          background: "#0a1f15", border: "1px solid #1a3a25",
          borderRadius: 6, color: "#2ecc71", fontSize: 13,
        }}>
          ✓ No hay alertas pendientes en ninguna categoría.
        </div>
      )}

      {/* Categorías agrupadas por urgencia */}
      {/* Toast undo flotante (7s) */}
      {undoToast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%",
          transform: "translateX(-50%)", zIndex: 1100,
          background: "#0d2a15", border: "1px solid #2ecc71",
          borderRadius: 8, padding: "10px 16px",
          display: "flex", alignItems: "center", gap: 14,
          fontSize: 12, color: "#ddd",
          boxShadow: "0 6px 20px rgba(0,0,0,0.5)",
          minWidth: "min(320px, 92vw)", maxWidth: "94vw", flexWrap: "wrap",
        }}>
          <span style={{ color: "#2ecc71", fontWeight: 700 }}>✓ Resuelta</span>
          <span style={{ color: "#cbd5e1", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {undoToast.categoryLabel}
          </span>
          <button onClick={handleUndo}
            style={{
              background: "#a78bfa", color: "#1a1535",
              border: "none", borderRadius: 5,
              padding: "5px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer",
            }}>↶ Deshacer</button>
        </div>
      )}

      {summary && conteos.total > 0 && (
        <div>
          {["critica", "alta", "media", "baja"].map(urg => {
            const cats = categoriasVisibles.filter(c => c.urgencia === urg);
            if (cats.length === 0) return null;
            const meta = URGENCIA_META[urg];
            const urgTotal = cats.reduce((s, c) => s + (summary[c.type] || 0), 0);
            // Si filtro está activo en otra urgencia, ocultar el grupo entero
            if (filtroUrgencia !== "todas" && filtroUrgencia !== urg) return null;
            return (
              <div key={urg} style={{ marginBottom: 18 }}>
                <div style={{
                  fontSize: 10, fontWeight: 700, color: meta.color,
                  textTransform: "uppercase", letterSpacing: ".07em",
                  marginBottom: 8, paddingLeft: 4,
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <span style={{
                    display: "inline-block", width: 8, height: 8,
                    background: meta.color, borderRadius: "50%",
                  }} />
                  {meta.label} <span style={{ color: "#999", fontWeight: 400 }}>· {urgTotal}</span>
                </div>
                {cats.map(cat => (
                  <CategoryCard key={cat.type} category={cat}
                    count={summary[cat.type] || 0}
                    onResolved={load}
                    onUndoableResolve={handleUndoableResolve} />
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Pulido;
